<template>
  <div class="Transfer Card Card--top">
    <div class="Card-header">
      <button class="Card-headerControl Button Transfer-close" @click="$emit('close', true)">&times;</button>
      <h2 class="Card-title">Refund options for Ticket {{ ticket.code }}</h2>
    </div>
    <div class="Card-body">
      <h3>{{ticket.code}}: {{ ticket.quantity }} x tickets for {{ ticket.name }}</h3>
      <h4>
        {{ ticket.event.name }} -
        {{ ticket.event.date | moment("ddd Do MMM YYYY, HH:mm") }}
      </h4>
      <p>{{ ticket.event.currency_symbol }}{{ ticket.event.price }}
        <span v-if="ticket.event.includes_booking_fee">(includes booking fee)</span>
        <span v-else>(excludes booking fee, {{ ticket.event.currency_symbol }}{{ticket.event.total_price}} paid)</span>
      </p>
      
      <!-- Partial Refund Quantity Selector -->
      <div v-if="ticket.quantity > 1" class="PartialRefund-selector">
        <label for="quantityToRefund">
          How many tickets do you want to refund?
          <input 
            type="number" 
            id="quantityToRefund"
            name="quantityToRefund"
            v-model="quantityToRefund"
            :min="1"
            :max="ticket.quantity"
            @blur="validateQuantity"
          />
        </label>
        <span v-if="quantityToRefund > 0 && quantityToRefund < ticket.quantity" class="PartialRefund-remaining">
          {{ ticket.quantity - quantityToRefund }} ticket<span v-if="ticket.quantity - quantityToRefund !== 1">s</span> will remain active
        </span>
      </div>
      
      <!-- Partial Refund Preview -->
      <div v-if="ticket.quantity > 1 && quantityToRefund > 0 && quantityToRefund <= ticket.quantity" class="PartialRefund-preview">
        <h4>Refund Summary</h4>
        <div class="PartialRefund-breakdown">
          <p>Quantity to refund: {{ quantityToRefund }}</p>
          <p>Ticket price: {{ ticket.event.currency_symbol }}{{ ticket.event.price }}</p>
          <p v-if="shouldShowBookingFeePreview">Booking fee: {{ ticket.event.currency_symbol }}{{ bookingFeePerTicket.toFixed(2) }}</p>
          <p><strong>Total refund: {{ ticket.event.currency_symbol }}{{ refundPreviewTotal }}</strong></p>
        </div>
      </div>

      <form @submit="cancelTicket($event)">
        <div v-if="ticket.event.price > 0">
          <label>
            <input type="checkbox" name="isRefund" v-model="isRefund"> Refund customer
          </label>
          <label v-if="!ticket.event.includes_booking_fee">
            <input type="checkbox" name="refundFee" v-model="refundFee"> Refund booking fee
          </label>
          <label>
            <!-- select reason for refund -->
            <select name="refundReason" v-model="reason">
              <option value="">Select a reason</option>
              <option value="duplicate">Duplicate order</option>
              <option value="requested_by_customer">Requested by customer</option>
              <option value="fraudulent">Fraudulent</option>
            </select>
          </label>
        </div>

        <button
          class="Button"
          :disabled="isDisabled"
          type="submit">Cancel ticket<span v-if="ticket.quantity > 1">(s)</span>
        </button>

        <p v-if="asyncFormError" class="Error">{{ asyncFormError }}</p>
        <a v-if="reissueTicketEmailUrl" :href="reissueTicketEmailUrl" target="_blank">
          Reissue ticket email with new event details
        </a>
      </form>
    </div>
  </div>
</template>

<script>
import cookieModel from '../models/cookie';
import axios from "axios";
import errorModel from "../models/error";
import toastModel from '../models/toast';
import moment from 'moment';

export default {
  filters: {
    moment: function (date, format) {
      return moment(date).format(format ?? 'MMMM Do YYYY, h:mm:ss a');
    }
  },
  props: {
    ticket: {
      type: Object,
      required: true,
      default: function() {
        return {
          code: '',
          name: '',
          quantity: 0,
          event: {
            name: '',
            date: '',
            price: 0,
            currency_symbol: '',
            includes_booking_fee: false,
            total_price: 0
          }
        }
      }
    },
  },
  computed: {
    isDisabled: function () {
      // For partial refunds, also check if quantityToRefund is valid
      if (this.ticket.quantity > 1) {
        return this.disableForm || this.reason === '' || this.quantityToRefund < 1 || this.quantityToRefund > this.ticket.quantity;
      }
      return this.disableForm || this.reason === '';
    },
    // Calculated quantity for refund (either full or partial)
    actualRefundQuantity: function () {
      const quantity = this.ticket.quantity > 1 ? parseInt(this.quantityToRefund, 10) : parseInt(this.ticket.quantity, 10);
      return isNaN(quantity) ? 0 : quantity;
    },
    bookingFeePerTicket: function () {
      const ticketPrice = parseFloat(this.ticket.event.price);
      const totalPrice = parseFloat(this.ticket.event.total_price);
      if (isNaN(ticketPrice) || isNaN(totalPrice)) {
        return 0;
      }
      const fee = totalPrice - ticketPrice;
      return fee > 0 ? fee : 0;
    },
    shouldShowBookingFeePreview: function () {
      const includesFee = Number(this.ticket.event.includes_booking_fee) === 1;
      return !includesFee && this.refundFee && this.bookingFeePerTicket > 0;
    },
    refundPreviewTotal: function () {
      const quantity = this.actualRefundQuantity;
      if (quantity <= 0) {
        return '0.00';
      }
      const ticketPrice = parseFloat(this.ticket.event.price);
      const basePrice = isNaN(ticketPrice) ? 0 : ticketPrice;
      const includesFee = Number(this.ticket.event.includes_booking_fee) === 1;
      const perTicketTotal = includesFee || !this.refundFee
        ? basePrice
        : basePrice + this.bookingFeePerTicket;
      return (perTicketTotal * quantity).toFixed(2);
    }
  },
  watch: {
    'ticket.quantity': function (newQuantity) {
      const parsedQuantity = parseInt(newQuantity, 10);
      if (!isNaN(parsedQuantity) && parsedQuantity > 0) {
        this.quantityToRefund = parsedQuantity;
      } else {
        this.quantityToRefund = 1;
      }
    }
  },
  methods: {
    validateQuantity: function () {
      // Ensure quantityToRefund stays within valid bounds
      if (!this.quantityToRefund || this.quantityToRefund < 1) {
        this.quantityToRefund = 1;
      } else if (this.quantityToRefund > this.ticket.quantity) {
        this.quantityToRefund = this.ticket.quantity;
      }
    },
    cancelTicket: function (e) {
      e.preventDefault();
      const vm = this;
      // Add authorisation to request header
      vm.axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' +
        cookieModel.getCookie('ticketlab_access');

      // send refund request to API
      let url = vm.baseUrl + '/einstein/ticket/cancel';
      let postData = {
        ticketCode: vm.ticket.code,
        eventId: vm.ticket.event_id,
      };

      // If price is greater than 0, we need to know if we're refunding the fee
      if (vm.ticket.event.price > 0) {
        postData.refundFee = vm.refundFee;
        postData.isRefund = vm.isRefund;
        postData.reason = vm.reason;
      }

      // Add partial refund support: include quantity to refund
      if (vm.ticket.quantity > 1) {
        postData.quantityToRefund = vm.actualRefundQuantity;
      }

      vm.disableForm = true;

      vm.axiosInstance.post(url, postData)
        .then(function (response) {
          vm.asyncFormError = '';
          if (response.data.status === 'error') {
            vm.asyncFormError = response.data.reason;
          } else {
            vm.asyncFormError = 'Success!'
            const msg = vm.isRefund
              ? `Ticket ${vm.ticket.code} cancelled/refunded`
              : `Ticket ${vm.ticket.code} cancelled`;
            toastModel.show(msg);
            if (response.data.url) {
              vm.reissueTicketEmailUrl = response.data.url;
            }
          }
          vm.disableForm = false;
        }.bind(this))
        .catch(function (error) {
          vm.asyncFormError = errorModel.apiError(error, vm.$router);
          vm.disableForm = false;
        });
    }
  },
  data: function () {
    return {
      baseUrl: process.env.VUE_APP_API_URL,
      venueId: process.env.VUE_APP_VENUE_ID,
      axiosInstance: axios.create({
        baseURL: this.baseUrl,
      }),
      targetEvent: false,
      disableForm: false,
      asyncFormError: false,
      reissueTicketEmailUrl: false,
      refundFee: false,
      isRefund: true,
      reason: "",
      quantityToRefund: this.ticket.quantity || 1,
    }
  }
}
</script>

<style scoped>
.PartialRefund-selector {
  margin: 20px 0;
  padding: 15px;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.PartialRefund-selector label {
  display: block;
  margin-bottom: 10px;
  font-weight: bold;
}

.PartialRefund-selector input {
  margin-left: 10px;
  padding: 8px;
  width: 80px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.PartialRefund-remaining {
  display: block;
  margin-top: 10px;
  color: #666;
  font-style: italic;
}

.PartialRefund-preview {
  margin: 20px 0;
  padding: 15px;
  background-color: #e8f5e9;
  border: 1px solid #4caf50;
  border-radius: 4px;
}

.PartialRefund-preview h4 {
  margin-top: 0;
  color: #2e7d32;
}

.PartialRefund-breakdown p {
  margin: 8px 0;
}
</style>
